/**
 * Web Vitals Performance Monitoring for ArbiWin
 * Tracks Core Web Vitals and custom performance metrics
 *
 * Following constitution requirements for performance monitoring
 */

/**
 * Report Web Vitals metrics
 * @param {Function} onPerfEntry - Callback to handle performance entries
 */
function reportWebVitals(onPerfEntry) {
  if (onPerfEntry && typeof onPerfEntry === 'function') {
    // Dynamic import to avoid loading web-vitals in production if not needed
    import('web-vitals').then(function(webVitals) {
      // Core Web Vitals
      webVitals.getCLS(onPerfEntry);  // Cumulative Layout Shift
      webVitals.getFID(onPerfEntry);  // First Input Delay
      webVitals.getFCP(onPerfEntry);  // First Contentful Paint
      webVitals.getLCP(onPerfEntry);  // Largest Contentful Paint
      webVitals.getTTFB(onPerfEntry); // Time to First Byte
    }).catch(function(error) {
      console.warn('Web Vitals could not be loaded:', error);
    });
  }
}

/**
 * Custom ArbiWin performance metrics
 */
export const ArbiWinMetrics = {
  // Track surebet data loading performance
  trackSurebetDataLoad: function(startTime, dataCount) {
    const endTime = performance.now();
    const loadTime = endTime - startTime;

    const metric = {
      name: 'surebet-data-load',
      value: loadTime,
      dataCount: dataCount,
      timestamp: Date.now()
    };

    this.reportCustomMetric(metric);

    return metric;
  },

  // Track calculator performance
  trackCalculatorPerformance: function(startTime, calculationType) {
    const endTime = performance.now();
    const calculationTime = endTime - startTime;

    const metric = {
      name: 'calculator-performance',
      value: calculationTime,
      type: calculationType,
      timestamp: Date.now()
    };

    this.reportCustomMetric(metric);

    return metric;
  },

  // Track API response times
  trackApiResponse: function(endpoint, responseTime, status) {
    const metric = {
      name: 'api-response-time',
      value: responseTime,
      endpoint: endpoint,
      status: status,
      timestamp: Date.now()
    };

    this.reportCustomMetric(metric);

    return metric;
  },

  // Track user interactions
  trackUserInteraction: function(action, component, timing) {
    const metric = {
      name: 'user-interaction',
      action: action,
      component: component,
      timing: timing,
      timestamp: Date.now()
    };

    this.reportCustomMetric(metric);

    return metric;
  },

  // Report custom metrics
  reportCustomMetric: function(metric) {
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 ArbiWin Metric:', metric);
    }

    // Store metrics for potential analytics
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        let existingMetrics = JSON.parse(localStorage.getItem('arbiwin_metrics') || '[]');
        existingMetrics.push(metric);

        // Keep only last 100 metrics to avoid storage bloat
        if (existingMetrics.length > 100) {
          existingMetrics = existingMetrics.slice(-100);
        }

        localStorage.setItem('arbiwin_metrics', JSON.stringify(existingMetrics));
      } catch (error) {
        console.warn('Could not store performance metric:', error);
      }
    }

    // Send to analytics service if configured
    if (window.gtag && process.env.REACT_APP_GA_MEASUREMENT_ID) {
      window.gtag('event', metric.name, {
        custom_parameter_value: metric.value,
        custom_parameter_component: metric.component || 'unknown',
        custom_parameter_action: metric.action || 'unknown'
      });
    }
  },

  // Get performance summary
  getPerformanceSummary: function() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return { metrics: [], summary: null };
    }

    try {
      const metrics = JSON.parse(localStorage.getItem('arbiwin_metrics') || '[]');

      const summary = {
        totalMetrics: metrics.length,
        avgSurebetLoadTime: 0,
        avgCalculatorTime: 0,
        avgApiResponseTime: 0,
        slowestOperations: []
      };

      // Calculate averages
      const surebetMetrics = metrics.filter(function(m) { return m.name === 'surebet-data-load'; });
      const calculatorMetrics = metrics.filter(function(m) { return m.name === 'calculator-performance'; });
      const apiMetrics = metrics.filter(function(m) { return m.name === 'api-response-time'; });

      if (surebetMetrics.length > 0) {
        summary.avgSurebetLoadTime = surebetMetrics.reduce(function(sum, m) { return sum + m.value; }, 0) / surebetMetrics.length;
      }

      if (calculatorMetrics.length > 0) {
        summary.avgCalculatorTime = calculatorMetrics.reduce(function(sum, m) { return sum + m.value; }, 0) / calculatorMetrics.length;
      }

      if (apiMetrics.length > 0) {
        summary.avgApiResponseTime = apiMetrics.reduce(function(sum, m) { return sum + m.value; }, 0) / apiMetrics.length;
      }

      // Find slowest operations
      summary.slowestOperations = metrics
        .sort(function(a, b) { return b.value - a.value; })
        .slice(0, 5);

      return { metrics: metrics, summary: summary };
    } catch (error) {
      console.warn('Could not get performance summary:', error);
      return { metrics: [], summary: null };
    }
  },

  // Clear stored metrics
  clearMetrics: function() {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('arbiwin_metrics');
    }
  }
};

/**
 * Development mode performance monitoring
 */
if (process.env.NODE_ENV === 'development') {
  // Log performance summary on page load
  window.addEventListener('load', function() {
    setTimeout(function() {
      const summary = ArbiWinMetrics.getPerformanceSummary();
      if (summary.summary && summary.summary.totalMetrics > 0) {
        console.log('📈 ArbiWin Performance Summary:', summary.summary);
      }
    }, 2000);
  });

  // Monitor memory usage
  if (window.performance && window.performance.memory) {
    setInterval(function() {
      const memory = window.performance.memory;
      if (memory.usedJSHeapSize > 50 * 1024 * 1024) { // 50MB threshold
        console.warn('⚠️ High memory usage detected:', {
          used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
          total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + 'MB'
        });
      }
    }, 30000); // Check every 30 seconds
  }
}

export default reportWebVitals;
